import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Activity, Wallet, Building2, Shuffle, HelpCircle, Info, X } from "lucide-react";
import { Button } from "../components/ui/button";
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

// Custom node components with handles
const WalletNode = ({ data }) => {
  const riskColors = {
    low: "border-emerald-500 bg-emerald-50",
    medium: "border-amber-500 bg-amber-50",
    high: "border-rose-500 bg-rose-50",
  };

  return (
    <div className={`w-11 h-11 sm:w-14 sm:h-14 rounded-full border-2 flex items-center justify-center shadow-lg ${riskColors[data.risk_level] || riskColors.low}`}>
      <Handle type="target" position={Position.Top} className="!bg-slate-400 !w-2 !h-2" />
      <Handle type="source" position={Position.Bottom} className="!bg-slate-400 !w-2 !h-2" />
      <Wallet className="w-4 h-4 sm:w-6 sm:h-6 text-slate-600" />
      {data.label && (
        <div className="absolute -bottom-6 sm:-bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] sm:text-xs font-semibold text-slate-700 bg-white px-1.5 py-0.5 sm:px-2 sm:py-1 rounded shadow-md border border-slate-200 max-w-[80px] sm:max-w-none truncate">
          {data.label}
        </div>
      )}
    </div>
  );
};

// Real exchange logos
const exchangeLogos = {
  binance: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/270.png",
  bybit: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/521.png",
  coinbase: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/89.png",
  kraken: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/24.png",
  okx: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/294.png",
  huobi: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/102.png",
  kucoin: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/311.png",
  "gate.io": "https://s2.coinmarketcap.com/static/img/exchanges/64x64/302.png",
  bitfinex: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/37.png",
  bitstamp: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/70.png",
  gemini: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/151.png",
  "crypto.com": "https://s2.coinmarketcap.com/static/img/exchanges/64x64/1149.png",
  mexc: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/544.png",
  bitget: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/513.png",
  htx: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/102.png",
  poloniex: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/43.png",
  upbit: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/351.png",
  bithumb: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/200.png",
  whitebit: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/482.png",
  bingx: "https://s2.coinmarketcap.com/static/img/exchanges/64x64/1110.png",
  uniswap: "https://s2.coinmarketcap.com/static/img/coins/64x64/7083.png",
  pancakeswap: "https://s2.coinmarketcap.com/static/img/coins/64x64/7186.png",
  sushiswap: "https://s2.coinmarketcap.com/static/img/coins/64x64/6758.png",
  curve: "https://s2.coinmarketcap.com/static/img/coins/64x64/6538.png",
  "1inch": "https://s2.coinmarketcap.com/static/img/coins/64x64/8104.png",
  dydx: "https://s2.coinmarketcap.com/static/img/coins/64x64/11156.png",
  gmx: "https://s2.coinmarketcap.com/static/img/coins/64x64/11857.png",
  jupiter: "https://s2.coinmarketcap.com/static/img/coins/64x64/29210.png",
  raydium: "https://s2.coinmarketcap.com/static/img/coins/64x64/8526.png",
  balancer: "https://s2.coinmarketcap.com/static/img/coins/64x64/5728.png",
  traderjoe: "https://s2.coinmarketcap.com/static/img/coins/64x64/11396.png",
  orca: "https://s2.coinmarketcap.com/static/img/coins/64x64/11165.png",
  velodrome: "https://s2.coinmarketcap.com/static/img/coins/64x64/21074.png",
  camelot: "https://s2.coinmarketcap.com/static/img/coins/64x64/23254.png",
  flux4af: "https://s2.coinmarketcap.com/static/img/coins/64x64/3029.png",
  "fintech platform": "/fintech-platform.png",
  "zyphora platform": "/zyphora-platform.png",
};

const ExchangeNode = ({ data }) => {
  const name = (data.label || data.exchange_name || "").toLowerCase();
  const logo = exchangeLogos[name];
  
  return (
    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl bg-white border-2 border-blue-400 flex items-center justify-center shadow-lg ring-2 sm:ring-4 ring-blue-50 overflow-hidden">
      <Handle type="target" position={Position.Top} className="!bg-blue-400 !w-2 !h-2" />
      <Handle type="source" position={Position.Bottom} className="!bg-blue-400 !w-2 !h-2" />
      {logo ? (
        <img src={logo} alt={data.label} className="w-8 h-8 sm:w-10 sm:h-10 object-contain rounded-lg" />
      ) : (
        <Building2 className="w-5 h-5 sm:w-7 sm:h-7 text-blue-500" />
      )}
      {(data.label || data.exchange_name) && (
        <div className="absolute -bottom-6 sm:-bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] sm:text-xs font-semibold text-blue-700 bg-blue-50 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded border border-blue-200 capitalize max-w-[70px] sm:max-w-none truncate">
          {data.label || data.exchange_name}
        </div>
      )}
    </div>
  );
};

const MixerNode = ({ data }) => (
  <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-lg bg-rose-50 border-2 border-rose-400 flex items-center justify-center shadow-lg">
    <Handle type="target" position={Position.Top} className="!bg-rose-400 !w-2 !h-2" />
    <Handle type="source" position={Position.Bottom} className="!bg-rose-400 !w-2 !h-2" />
    <Shuffle className="w-4 h-4 sm:w-6 sm:h-6 text-rose-500" />
    <div className="absolute -bottom-6 sm:-bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] sm:text-xs font-semibold text-rose-700 bg-rose-50 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded border border-rose-200">
      Mixer
    </div>
  </div>
);

const UnknownNode = ({ data }) => (
  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-slate-50 border-2 border-slate-300 flex items-center justify-center shadow-md">
    <Handle type="target" position={Position.Top} className="!bg-slate-400 !w-2 !h-2" />
    <Handle type="source" position={Position.Bottom} className="!bg-slate-400 !w-2 !h-2" />
    <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
    {data.label && (
      <div className="absolute -bottom-6 sm:-bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] sm:text-xs text-slate-600 bg-white px-1.5 py-0.5 sm:px-2 sm:py-1 rounded shadow-sm border border-slate-200 max-w-[70px] sm:max-w-none truncate">
        {data.label}
      </div>
    )}
  </div>
);

const RoutePage = () => {
  const { routeId } = useParams();
  const navigate = useNavigate();
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [showLegend, setShowLegend] = useState(false);

  const nodeTypes = useMemo(() => ({
    wallet: WalletNode,
    exchange: ExchangeNode,
    mixer: MixerNode,
    unknown: UnknownNode,
  }), []);

  useEffect(() => {
    const fetchRoute = async () => {
      try {
        const response = await axios.get(`${API}/route/${routeId}`);
        setRoute(response.data);
        
        const flowNodes = response.data.nodes.map((node) => ({
          id: node.node_id,
          type: node.node_type || "unknown",
          position: { x: node.position_x || 0, y: node.position_y || 0 },
          data: {
            label: node.label,
            address: node.address,
            exchange_name: node.exchange_name,
            risk_level: node.risk_level,
          },
        }));

        const flowEdges = response.data.edges.map((edge) => ({
          id: edge.edge_id,
          source: edge.source_node_id,
          target: edge.target_node_id,
          label: edge.amount ? `${edge.amount} ${edge.token}` : "",
          labelStyle: { fontSize: 8, fontFamily: "JetBrains Mono", fill: "#475569" },
          labelBgStyle: { fill: "white", fillOpacity: 0.9 },
          labelBgPadding: [3, 1],
          style: { stroke: "#94A3B8", strokeWidth: 1.5 },
          markerEnd: { type: MarkerType.ArrowClosed, color: "#64748B", width: 16, height: 16 },
          animated: true,
        }));

        setNodes(flowNodes);
        setEdges(flowEdges);
      } catch (error) {
        toast.error("Маршрут не найден");
        navigate("/");
      } finally {
        setLoading(false);
      }
    };
    fetchRoute();
  }, [routeId, navigate, setNodes, setEdges]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!route) return null;

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="route-page">
      {/* Header - Mobile responsive */}
      <header className="header-blur border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2.5 sm:py-4">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn" className="px-2 sm:px-3 shrink-0">
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Назад</span>
            </Button>
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-slate-900 rounded-lg flex items-center justify-center shrink-0">
              <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-sm sm:text-xl font-bold text-slate-900 truncate">{route.name}</h1>
              {route.description && (
                <p className="text-[10px] sm:text-xs text-slate-500 truncate">{route.description}</p>
              )}
            </div>
            <div className="flex items-center gap-2 sm:gap-4 text-[10px] sm:text-sm text-slate-500 shrink-0">
              <span className="flex flex-col sm:flex-row sm:gap-1 items-center">
                <span className="font-bold text-slate-700">{route.nodes?.length || 0}</span>
                <span className="hidden sm:inline">узлов</span>
              </span>
              <span className="flex flex-col sm:flex-row sm:gap-1 items-center">
                <span className="font-bold text-slate-700">{route.edges?.length || 0}</span>
                <span className="hidden sm:inline">связей</span>
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* React Flow Canvas */}
      <div className="flex-1 relative" style={{ minHeight: "calc(100vh - 56px)" }}>
        {nodes.length === 0 ? (
          <div className="h-full flex items-center justify-center text-slate-500">
            Маршрут пуст. Добавьте узлы в админ-панели.
          </div>
        ) : (
          <div className="w-full h-full absolute inset-0">
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.4, minZoom: 0.15, maxZoom: 1.2 }}
              minZoom={0.1}
              maxZoom={2}
              attributionPosition="bottom-right"
            >
              <Background color="#E2E8F0" gap={20} size={1} />
              <Controls 
                className="bg-white border border-slate-200 rounded-lg shadow-sm"
                position="top-right"
                showInteractive={false}
              />
            </ReactFlow>
          </div>
        )}

        {/* Legend toggle button (mobile) */}
        <button
          onClick={() => setShowLegend(!showLegend)}
          className="absolute bottom-3 left-3 z-10 w-9 h-9 sm:hidden bg-white border border-slate-200 rounded-lg shadow-lg flex items-center justify-center"
          data-testid="legend-toggle"
        >
          <Info className="w-4 h-4 text-slate-600" />
        </button>

        {/* Legend - collapsible on mobile */}
        <div className={`absolute bottom-3 left-3 sm:left-4 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-lg shadow-lg z-10 transition-all ${showLegend ? 'p-3 opacity-100' : 'p-3 hidden sm:block sm:opacity-100'}`}>
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <h4 className="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-wide">Легенда</h4>
            <button onClick={() => setShowLegend(false)} className="sm:hidden p-0.5">
              <X className="w-3 h-3 text-slate-400" />
            </button>
          </div>
          <div className="space-y-1.5 sm:space-y-2">
            <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs">
              <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-emerald-500 bg-emerald-50 shrink-0"></div>
              <span className="text-slate-600">Низкий риск</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs">
              <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-amber-500 bg-amber-50 shrink-0"></div>
              <span className="text-slate-600">Средний риск</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs">
              <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-rose-500 bg-rose-50 shrink-0"></div>
              <span className="text-slate-600">Высокий риск</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs">
              <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-lg border-2 border-blue-400 bg-blue-50 shrink-0"></div>
              <span className="text-slate-600">Биржа / DEX</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoutePage;
